<template>
  <div class="config-panel" :style="styles">
    <div class="config-panel__contents">
      <div class="config-panel__overview">
        <div class="config-panel__title">
          {{ tags }}
        </div>
        <span class="config-panel__value">
          {{ stringValue }}
        </span>
        <span class="config-panel__unit">
          {{ stat.unit }}&nbsp;
        </span>
      </div>
      <div class="config-panel__chart-wrapper">
        <line-chart
          :chartData="chartData"
          :options="options"
          :rows="viewport.rowCnt"
        />
      </div>
    </div>
  </div>
</template>

<script>
import millify from 'millify'
import LineChart from '../LineChart.vue'

const palette = {
  red: '#FF7A7F',
  blue: '#619EFF',
  green: '#95EDAA'
}

export default {
  components: {
    LineChart
  },
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    series: {
      type: Object,
      default: () => ({})
    },
    palette: {
      type: Object,
      default: () => ({})
    },
    viewport: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    styles () {
      return {
        '--text-color': this.config.textColor,
        '--background-color': palette[this.color] || this.color,
        '--width': `${100 / this.viewport.colCnt - 1}%`,
        '--height': `${100 / this.viewport.rowCnt - 1}%`
      }
    },
    options () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 0,
        legend: { display: false },
        elements: {
          point: {
            radius: 0
          }
        },
        scales: {
          xAxes: [{ type: 'time', display: false }],
          yAxes: [{ display: false }]
        },
        tooltips: {
          mode: 'nearest'
        }
      }
    },
    chartData () {
      return {
        labels: this.series?.pointlist.map(([t]) => t),
        datasets: [{
          pointHitRadius: 10,
          backgroundColor: '#FFF4',
          borderColor: '#FFFC',
          borderWidth: 1,
          fill: true,
          data: this.series.pointlist.map(([,v]) => v)
        }]
      }
    },
    color () {
      for (const rule of this.palette.rules) {
        try {
          if (eval(`${this.value} ${rule.condition}`)) {
            return rule.background
          }
        } catch (err) { /***/ }
      }

      return this.palette.background
    },
    value () {
      return this.stat[this.config.reducer]
    },
    stringValue () {
      if (this.value === Infinity) { return 'N/A' }
      if (this.value === undefined) { return 'N/A' }
      return millify(this.value, { precision: 2 })
    },
    stat () {
      const valueList = (this.series?.pointlist || []).map(([, value]) => value)

      const min = Math.min(...valueList)
      const max = Math.max(...valueList)
      const sum = valueList.reduce((acc, cur) => acc + cur, 0)
      const avg = sum / valueList.length
      const last = valueList[valueList.length - 1]

      return {
        title: this.series?.display_name,
        avg,
        sum,
        min,
        max,
        last,
        unit: this.series?.unit?.[0]?.short_name || ''
      }
    },
    tags () {
      return (this.series?.tag_set || []).join(' ')
    }
  }
}
</script>

<style>
.config-panel {
  flex: 1 0;
  width: var(--width);
  height: var(--height);

  display: flex;
  padding: 4px;
}
.config-panel__contents {
  text-align: left;
  border-radius: 2px;
  overflow: hidden;

  flex: 1 0;
  display: flex;
  flex-direction: column;

  color: var(--text-color);
  background-color: var(--background-color);
  background-image: linear-gradient(90deg, transparent, #fff6);
}

.config-panel__overview {
  padding: 16px;
}

.config-panel__title {
  width: calc(100% - 32px);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.config-panel__value {
  font-size: 32px;
  font-weight: bold;
}

.config-panel__unit {
  font-size: 20px;
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.config-panel__chart-wrapper {
  margin-top: auto;
}

@media screen and (max-height: 150px) {
  .config-panel__chart-wrapper {
    display: none;
  }
}
</style>
